/**
 * Bellunio theme scripts.
 * Kept small on purpose: the theme works without JavaScript, this only enhances it.
 */

(() => {
  /* Header: expose the header height as a CSS variable for the mobile menu panel. */
  const setHeaderBottom = () => {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;
    const bottom = Math.max(0, header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
  };

  /* Close open <details> menus when clicking outside or pressing Escape. */
  document.addEventListener('click', (event) => {
    document
      .querySelectorAll('.menu-drawer[open], .header-search[open], .facets__filter[open]')
      .forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.menu-drawer[open], .header-search[open], .facets__filter[open]');
    if (!open) return;
    open.removeAttribute('open');
    open.querySelector('summary')?.focus();
  });

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (details.classList.contains('menu-drawer')) setHeaderBottom();
    if (details.classList.contains('header-search')) {
      details.querySelector('input[type="search"]')?.focus();
    }
  }, true);

  window.addEventListener('resize', setHeaderBottom, { passive: true });
  window.addEventListener('scroll', setHeaderBottom, { passive: true });
  document.addEventListener('DOMContentLoaded', setHeaderBottom);

  /* Quantity buttons (product page and cart). */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (!button) return;
    const input = button.closest('[data-quantity]')?.querySelector('input');
    if (!input) return;
    const min = Number(input.min || 0);
    const step = button.hasAttribute('data-quantity-plus') ? 1 : -1;
    input.value = Math.max(min, (Number(input.value) || 0) + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Cart: update quantities automatically. */
  let cartTimer;
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-cart-quantity]')) return;
    clearTimeout(cartTimer);
    cartTimer = setTimeout(() => event.target.form?.submit(), 500);
  });

  /* Collection filters and sorting: submit on change. */
  let facetsTimer;
  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    clearTimeout(facetsTimer);
    const delay = event.target.type === 'number' ? 700 : 0;
    facetsTimer = setTimeout(() => {
      const params = new URLSearchParams(new FormData(form));
      for (const [key, value] of [...params.entries()]) {
        if (value === '') params.delete(key);
      }
      window.location.search = params.toString();
    }, delay);
  });
})();

/**
 * Product page: variant selection, gallery and price updates.
 */
class ProductInfo extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    this.variants = json ? JSON.parse(json.textContent).variants : [];
    this.slider = this.querySelector('[data-slider]');

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-position]')) this.onOptionChange(event.target);
    });

    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumb));
    });

    this.slider?.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.syncThumbs(), 100);
    }, { passive: true });

    this.updateAvailability();
  }

  get selectedOptions() {
    return [...this.querySelectorAll('[data-variant-picker] fieldset')].map(
      (fieldset) => fieldset.querySelector('input:checked')?.value
    );
  }

  onOptionChange(input) {
    const fieldset = input.closest('fieldset');
    const label = fieldset?.querySelector('[data-option-value]');
    if (label) label.textContent = input.value;

    const options = this.selectedOptions;
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    this.updateAvailability();

    const button = this.querySelector('[data-update="add-button"]');
    if (!variant) {
      if (button) {
        button.disabled = true;
        button.textContent = window.theme?.strings?.unavailable || button.textContent;
      }
      return;
    }

    this.querySelectorAll('[data-variant-id]').forEach((field) => (field.value = variant.id));
    if (variant.featured_media) this.showMedia(variant.featured_media);

    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.pathname + url.search);

    this.renderVariant(variant.id);
  }

  /* Re-render price and add-to-cart button through the Section Rendering API. */
  async renderVariant(variantId) {
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const url = `${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.sectionId}`;
      const response = await fetch(url, { signal: this.abort.signal });
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      ['price', 'add-button'].forEach((key) => {
        const source = html.querySelector(`[data-update="${key}"]`);
        const target = this.querySelector(`[data-update="${key}"]`);
        if (!source || !target) return;
        target.innerHTML = source.innerHTML;
        if (key === 'add-button') target.disabled = source.disabled;
      });
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  /* Mark option values that have no available variant with the current selection. */
  updateAvailability() {
    const selected = this.selectedOptions;
    this.querySelectorAll('[data-option-position]').forEach((input) => {
      const position = Number(input.dataset.optionPosition) - 1;
      const available = this.variants.some(
        (variant) =>
          variant.available &&
          variant.options[position] === input.value &&
          variant.options.every((value, i) => i === position || selected[i] === undefined || value === selected[i])
      );
      input.classList.toggle('is-unavailable', !available);
    });
  }

  showMedia(mediaId) {
    const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide || !this.slider) return;
    this.slider.scrollTo({ left: slide.offsetLeft - this.slider.offsetLeft, behavior: 'smooth' });
    this.setCurrentThumb(String(mediaId));
  }

  syncThumbs() {
    if (!this.slider) return;
    const index = Math.round(this.slider.scrollLeft / this.slider.clientWidth);
    const slide = this.slider.children[index];
    if (slide) this.setCurrentThumb(slide.dataset.mediaId);
  }

  setCurrentThumb(mediaId) {
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      if (thumb.dataset.thumb === mediaId) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

/**
 * Related products, loaded when the section scrolls into view.
 */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = html.querySelector('product-recommendations');
      if (source && source.innerHTML.trim().length) {
        this.innerHTML = source.innerHTML;
      } else {
        this.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
